
import { useDataEngine } from '@dhis2/app-runtime';


// Role hierarchy constants (kept for reference but not used in simple approach)
export const roleRanks = {
  R3lFolCMdoi: 0, // Admin
  nLSSLch316I: 1, // CEO
  adeqQmxfCLF: 2, // HOD
  gtMJNK0QVL7: 3, // Top Staff
  vdVjNaqxblC: 4, // Senior Staff
  Guest: 99,
};

// Cache for user roles to avoid repeated API calls
const userRoleCache = new Map();
const usernameCache = new Map();

/**
 * Fetches the role ID for a given username
 */
export const fetchUserRole = async (engine, username) => {
  if (!username) return null;
  
  // Check cache first
  if (userRoleCache.has(username)) {
    return userRoleCache.get(username);
  }

  try {
    const { users } = await engine.query({
      users: {
        resource: 'users',
        params: {
          filter: `username:eq:${username}`,
          fields: 'id,userCredentials[userRoles[id,name]]',
          pageSize: 1
        }
      }
    });

    const role = users?.users?.[0]?.userCredentials?.userRoles?.[0]?.id || null;
    userRoleCache.set(username, role);
    return role;
  } catch (error) {
    console.error('Error fetching user role:', error);
    userRoleCache.set(username, null);
    return null;
  }
};

export const fetchUsername = async (engine, userId) => {
  if (!userId) return null;
  
  // Check cache first
  if (usernameCache.has(userId)) {
    return usernameCache.get(userId);
  }

  try {
    const { users } = await engine.query({
      users: {
        resource: 'users',
        params: {
          filter: `id:eq:${userId}`,
          fields: 'userCredentials[username]',
          pageSize: 1
        }
      }
    });

    const username = users?.users?.[0]?.userCredentials?.username || null;
    usernameCache.set(userId, username);
    return username;
  } catch (error) {
    console.error('Error fetching username:', error);
    usernameCache.set(userId, null);
    return null;
  }
};

/**
 * Simple objective visibility check - users can see their own objectives only
 * CEO can only see their own objectives (not all objectives)
 */
export const shouldShowObjective = (currentUserRoleId, currentUsername, objectiveCreatorUsername) => {
  if (!currentUserRoleId || !currentUsername || !objectiveCreatorUsername) return false;
  
  // Users can see their own objectives only (including CEO)
  return currentUsername.toLowerCase() === objectiveCreatorUsername.toLowerCase();
};

/**
 * Gets the current user's information including role
 */
export const getCurrentUserWithRole = async (engine) => {
  try {
    const response = await engine.query({
      me: {
        resource: 'me',
        params: {
          fields: 'id,displayName,userCredentials[username,userRoles[id,name]]'
        }
      }
    });
    
    const userData = response.me;
    const primaryRoleId = userData.userCredentials?.userRoles?.[0]?.id || null;
    
    return {
      id: userData.id,
      displayName: userData.displayName,
      username: userData.userCredentials?.username,
      roleId: primaryRoleId,
      roleRank: roleRanks[primaryRoleId] || 99
    };
  } catch (error) {
    console.error('Error fetching current user:', error);
    return null;
  }
};

/**
 * Process objectives with simple visibility check
 */
export const processObjectivesSimple = async (events, currentUser) => {
  const processedObjectives = [];
  
  if (!events || !currentUser || !currentUser.username) return processedObjectives;
  
  for (const event of events) {
    const dataValues = event.dataValues || [];
    
    // Get creator information
    const createdBy = event.createdBy || {};
    const creatorUsername = createdBy.username || '';
    
    // Use simple visibility check
    const isVisible = shouldShowObjective(
      currentUser.roleId, 
      currentUser.username, 
      creatorUsername
    );
    
    if (isVisible) {
      processedObjectives.push({
        id: event.event,
        name: dataValues.find(dv => dv.dataElement === 'EuN5ztFnXsQ')?.value || 'Unnamed Objective',
        description: dataValues.find(dv => dv.dataElement === 'nWqW5BrcgP1')?.value || '',
        orgUnit: event.orgUnit,
        orgUnitName: event.orgUnitName,
        trackedEntityInstance: event.trackedEntityInstance,
        startDate: event.eventDate,
        createdBy: creatorUsername
      });
    }
  }
  
  return processedObjectives;
};